import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import { getFunnelsByBrand, deleteFunnel, duplicateFunnel } from '../services/funnelService';
import { LayoutGrid, List, Plus, GitBranch, FolderOpen } from 'lucide-react';
import ConfirmationModal from '../components/ConfirmationModal';
import { FunnelFilters, FunnelCard, FunnelRow } from '../components/saved-funnels';

export default function SavedFunnels() {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { selectedBrand } = useApp();
    const [funnels, setFunnels] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('grid');
    const [funnelToDelete, setFunnelToDelete] = useState(null);

    // Filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [platformFilter, setPlatformFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortBy, setSortBy] = useState('recent');

    useEffect(() => {
        loadFunnels();
    }, [currentUser, selectedBrand]);

    const loadFunnels = async () => {
        if (!currentUser || !selectedBrand?.id) {
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            const loadedFunnels = await getFunnelsByBrand(selectedBrand.id);
            setFunnels(loadedFunnels);
        } catch (err) {
            console.error('Error loading funnels:', err);
            setError('Failed to load saved funnels');
        } finally {
            setIsLoading(false);
        }
    };

    // Filter and sort funnels
    const filteredFunnels = useMemo(() => {
        let result = [...funnels];

        // Search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(f =>
                f.name?.toLowerCase().includes(query)
            );
        }

        // Platform filter
        if (platformFilter !== 'all') {
            result = result.filter(f => f.platform === platformFilter);
        }

        // Status filter
        if (statusFilter !== 'all') {
            result = result.filter(f => f.status === statusFilter);
        }

        // Sort
        result.sort((a, b) => {
            switch (sortBy) {
                case 'oldest':
                    return (a.updatedAt?.toDate?.() || new Date(a.updatedAt || 0)) -
                           (b.updatedAt?.toDate?.() || new Date(b.updatedAt || 0));
                case 'alphabetical':
                    return (a.name || '').localeCompare(b.name || '');
                case 'recent':
                default:
                    return (b.updatedAt?.toDate?.() || new Date(b.updatedAt || 0)) -
                           (a.updatedAt?.toDate?.() || new Date(a.updatedAt || 0));
            }
        });

        return result;
    }, [funnels, searchQuery, platformFilter, statusFilter, sortBy]);

    const handleLoadFunnel = (funnelId) => {
        navigate(`/funnel-builder?id=${funnelId}`);
    };

    const handleNewFunnel = () => {
        navigate('/funnel-builder');
    };

    const handleDeleteClick = (funnelId) => {
        setFunnelToDelete(funnelId);
    };

    const handleConfirmDelete = async () => {
        if (!funnelToDelete) return;

        try {
            await deleteFunnel(funnelToDelete);
            loadFunnels();
        } catch (err) {
            console.error('Error deleting funnel:', err);
            setError('Failed to delete funnel. Please try again.');
        } finally {
            setFunnelToDelete(null);
        }
    };

    const handleDuplicate = async (funnelId) => {
        try {
            const funnel = funnels.find(f => f.id === funnelId);
            await duplicateFunnel(funnelId, funnel?.name ? `${funnel.name} (Copy)` : 'Untitled Funnel (Copy)');
            loadFunnels();
        } catch (err) {
            console.error('Error duplicating funnel:', err);
            setError('Failed to duplicate funnel. Please try again.');
        }
    };

    if (!selectedBrand) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '50vh',
                gap: 'var(--space-4)'
            }}>
                <FolderOpen size={48} style={{ color: 'var(--color-text-tertiary)' }} />
                <h2 style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
                    Select a Brand
                </h2>
                <p style={{ color: 'var(--color-text-tertiary)', textAlign: 'center' }}>
                    Please select a brand from the sidebar to view saved funnels.
                </p>
            </div>
        );
    }

    return (
        <div>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--space-6)'
            }}>
                <div>
                    <h1 style={{
                        margin: '0 0 var(--space-1) 0',
                        fontSize: '1.75rem',
                        fontWeight: 700,
                        color: 'var(--color-text-primary)'
                    }}>
                        Saved Funnels
                    </h1>
                    <p style={{
                        margin: 0,
                        color: 'var(--color-text-secondary)',
                        fontSize: '0.9rem'
                    }}>
                        Manage your advertising funnels for {selectedBrand?.name}
                    </p>
                </div>

                <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
                    {/* View toggle */}
                    <div style={{
                        display: 'flex',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-md)',
                        overflow: 'hidden'
                    }}>
                        <button
                            onClick={() => setViewMode('grid')}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                padding: 'var(--space-2) var(--space-3)',
                                border: 'none',
                                backgroundColor: viewMode === 'grid' ? 'var(--primary-100)' : 'transparent',
                                color: viewMode === 'grid' ? 'var(--primary-700)' : 'var(--color-text-secondary)',
                                cursor: 'pointer',
                                fontSize: '0.875rem'
                            }}
                        >
                            <LayoutGrid size={16} />
                            Grid
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                padding: 'var(--space-2) var(--space-3)',
                                border: 'none',
                                borderLeft: '1px solid var(--color-border)',
                                backgroundColor: viewMode === 'list' ? 'var(--primary-100)' : 'transparent',
                                color: viewMode === 'list' ? 'var(--primary-700)' : 'var(--color-text-secondary)',
                                cursor: 'pointer',
                                fontSize: '0.875rem'
                            }}
                        >
                            <List size={16} />
                            List
                        </button>
                    </div>

                    {/* Create new button */}
                    <button
                        onClick={handleNewFunnel}
                        className="btn btn-primary"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)'
                        }}
                    >
                        <Plus size={18} />
                        New Funnel
                    </button>
                </div>
            </div>

            {/* Filters */}
            <FunnelFilters
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                platformFilter={platformFilter}
                onPlatformChange={setPlatformFilter}
                statusFilter={statusFilter}
                onStatusChange={setStatusFilter}
                sortBy={sortBy}
                onSortChange={setSortBy}
                totalCount={funnels.length}
                filteredCount={filteredFunnels.length}
            />

            {/* Error message */}
            {error && (
                <div style={{
                    padding: 'var(--space-3) var(--space-4)',
                    backgroundColor: 'var(--error-100)',
                    color: 'var(--error-700)',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: 'var(--space-4)'
                }}>
                    {error}
                </div>
            )}

            {/* Loading state */}
            {isLoading && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 'var(--space-8)',
                    color: 'var(--color-text-tertiary)'
                }}>
                    Loading funnels...
                </div>
            )}

            {/* Empty state */}
            {!isLoading && filteredFunnels.length === 0 && (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 'var(--space-12)',
                    gap: 'var(--space-4)',
                    backgroundColor: 'var(--color-surface)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px dashed var(--color-border)'
                }}>
                    <GitBranch size={48} style={{ color: 'var(--color-text-tertiary)' }} />
                    <h3 style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
                        {funnels.length === 0 ? 'No funnels yet' : 'No matching funnels'}
                    </h3>
                    <p style={{
                        margin: 0,
                        color: 'var(--color-text-tertiary)',
                        textAlign: 'center',
                        maxWidth: '400px'
                    }}>
                        {funnels.length === 0
                            ? 'Create your first advertising funnel to get started with campaign planning.'
                            : 'Try adjusting your filters to find what you\'re looking for.'
                        }
                    </p>
                    {funnels.length === 0 && (
                        <button
                            onClick={handleNewFunnel}
                            className="btn btn-primary"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                marginTop: 'var(--space-2)'
                            }}
                        >
                            <Plus size={18} />
                            Create Your First Funnel
                        </button>
                    )}
                </div>
            )}

            {/* Grid view */}
            {!isLoading && filteredFunnels.length > 0 && viewMode === 'grid' && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: 'var(--space-4)'
                }}>
                    {filteredFunnels.map(funnel => (
                        <FunnelCard
                            key={funnel.id}
                            funnel={funnel}
                            onLoad={handleLoadFunnel}
                            onDuplicate={handleDuplicate}
                            onDelete={handleDeleteClick}
                        />
                    ))}
                </div>
            )}

            {/* List view */}
            {!isLoading && filteredFunnels.length > 0 && viewMode === 'list' && (
                <div style={{
                    backgroundColor: 'var(--color-surface)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--color-border)',
                    overflow: 'hidden'
                }}>
                    {/* List header */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-4)',
                        padding: 'var(--space-3) var(--space-4)',
                        borderBottom: '1px solid var(--color-border)',
                        backgroundColor: 'var(--color-bg-secondary)',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: 'var(--color-text-tertiary)',
                        textTransform: 'uppercase'
                    }}>
                        <span style={{ minWidth: '70px' }}>Platform</span>
                        <span style={{ flex: 1 }}>Name</span>
                        <span style={{ minWidth: '70px' }}>Status</span>
                        <span style={{ minWidth: '180px' }}>Structure</span>
                        <span style={{ minWidth: '100px', textAlign: 'right' }}>Updated</span>
                        <span style={{ width: '32px' }}></span>
                    </div>
                    {filteredFunnels.map(funnel => (
                        <FunnelRow
                            key={funnel.id}
                            funnel={funnel}
                            onLoad={handleLoadFunnel}
                            onDuplicate={handleDuplicate}
                            onDelete={handleDeleteClick}
                        />
                    ))}
                </div>
            )}

            {/* Delete confirmation modal */}
            <ConfirmationModal
                isOpen={!!funnelToDelete}
                onClose={() => setFunnelToDelete(null)}
                onConfirm={handleConfirmDelete}
                title="Delete Funnel?"
                message="Are you sure you want to delete this funnel? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
            />
        </div>
    );
}
